import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { toast } from 'sonner';
import { acceptTerms, counterTerms } from '../lib/api/terms';
import Spinner from './Spinner';
import { setLiveMessage } from './ScreenReaderLive';

const TermsCard = ({ request, token, userRole, onUpdate }) => {
  const [counterModalOpen, setCounterModalOpen] = useState(false);
  const [counterPct, setCounterPct] = useState('');
  const [counterNotes, setCounterNotes] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Optimistic UI state
  const [accepting, setAccepting] = useState(false);
  const [countering, setCountering] = useState(false);
  const [optimisticStatus, setOptimisticStatus] = useState(null);
  const [optimisticCommission, setOptimisticCommission] = useState(undefined);
  const [optimisticNotes, setOptimisticNotes] = useState(undefined);

  if (!request?.terms) {
    return null;
  }

  const { terms, termsHistory = [] } = request;
  
  // Derived view object for rendering (optimistic or real)
  const viewTerms = {
    status: optimisticStatus ?? terms.status,
    commissionPct: optimisticCommission ?? terms.commissionPct,
    notes: optimisticNotes ?? terms.notes
  };
  
  const isAccepted = viewTerms.status === 'ACCEPTED';
  
  // Determine who made the last action (PROPOSED or COUNTERED)
  const lastAction = termsHistory.length > 0 ? termsHistory[termsHistory.length - 1] : null;
  const lastActor = lastAction ? lastAction.actor : null;
  
  // Only the OTHER party can accept after a counter/proposal
  // If last actor was BUSINESS, only SALESPERSON can accept
  // If last actor was SALESPERSON, only BUSINESS can accept
  const canAccept = !isAccepted && (
    (lastActor === 'BUSINESS' && userRole === 'SALESPERSON') ||
    (lastActor === 'SALESPERSON' && userRole === 'BUSINESS') ||
    (lastActor === null) // Initial state, either can accept
  );

  const handleAccept = async () => {
    try {
      setAccepting(true);
      setOptimisticStatus('ACCEPTED');
      setLiveMessage('Accepting terms…');
      
      await acceptTerms(request.id, token);
      
      setAccepting(false);
      setLiveMessage('Terms accepted.');
      toast.success('Terms accepted');
      onUpdate();
    } catch (error) {
      setAccepting(false);
      setOptimisticStatus(null);
      setLiveMessage('Failed to accept terms.');
      toast.error(error.message || 'Failed to accept terms');
    }
  };

  const handleCounter = async () => {
    const pct = parseFloat(counterPct);
    if (isNaN(pct) || pct < 0 || pct > 60) {
      toast.error('Commission must be between 0 and 60%');
      return;
    }

    try {
      setCountering(true);
      setOptimisticStatus('COUNTERED');
      setOptimisticCommission(pct);
      setOptimisticNotes(counterNotes || null);
      setLiveMessage('Submitting counter offer…');
      setCounterModalOpen(false);
      
      await counterTerms(request.id, {
        commissionPct: pct,
        notes: counterNotes || null
      }, token);
      
      setCountering(false);
      setLiveMessage('Counter offer sent.');
      toast.success('Counter offer sent');
      setCounterPct('');
      setCounterNotes('');
      onUpdate();
    } catch (error) {
      setCountering(false);
      setOptimisticStatus(null);
      setOptimisticCommission(undefined);
      setOptimisticNotes(undefined);
      setLiveMessage('Failed to send counter.');
      toast.error(error.message || 'Failed to counter terms');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'PENDING': 'secondary',
      'COUNTERED': 'outline',
      'ACCEPTED': 'default'
    };

    const colors = {
      'PENDING': 'bg-gray-500/10 text-gray-400 border-gray-500/20',
      'COUNTERED': 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
      'ACCEPTED': 'bg-green-500/10 text-green-500 border-green-500/20'
    };

    return (
      <Badge variant={variants[status]} className={colors[status]}>
        {status}
      </Badge>
    );
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <CardTitle className="text-lg">Offer Terms</CardTitle>
            {getStatusBadge(viewTerms.status)}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current Terms */}
          <div>
            <Label className="text-sm text-brand-muted">Commission</Label>
            <p className="text-3xl font-bold text-brand-gold">{viewTerms.commissionPct}%</p>
          </div>

          {viewTerms.notes && (
            <div>
              <Label className="text-sm text-brand-muted">Notes</Label>
              <p className="text-sm text-brand-text">{viewTerms.notes}</p>
            </div>
          )}

          {/* Action Buttons */}
          {!isAccepted && (
            <div className="flex gap-2">
              <Button
                onClick={handleAccept}
                disabled={accepting || countering || !canAccept}
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="accept-terms-button"
                aria-busy={accepting}
                title={!canAccept ? "You cannot accept your own offer" : ""}
              >
                {accepting && <Spinner className="mr-2" />}
                {accepting ? 'Accepting…' : 'Accept Terms'}
              </Button>
              <Button
                onClick={() => {
                  setCounterPct(viewTerms.commissionPct.toString());
                  setCounterNotes(viewTerms.notes || '');
                  setCounterModalOpen(true);
                }}
                variant="outline"
                disabled={accepting || countering}
                className="flex-1"
                data-testid="counter-terms-button"
              >
                Counter Offer
              </Button>
            </div>
          )}

          {isAccepted && !accepting && (
            <Alert className="bg-green-500/10 border-green-500/20">
              <AlertDescription className="text-green-500">
                ✓ Terms have been accepted by both parties
              </AlertDescription>
            </Alert>
          )}
          
          {accepting && (
            <Alert className="border-brand-gold/40">
              <AlertDescription className="text-brand-gold">
                Processing acceptance…
              </AlertDescription>
            </Alert>
          )}

          {countering && (
            <Alert className="border-brand-gold/40">
              <AlertDescription className="text-brand-gold">
                Sending counter offer…
              </AlertDescription>
            </Alert>
          )}

          {/* Terms History */}
          {termsHistory.length > 0 && (
            <Accordion type="single" collapsible>
              <AccordionItem value="history">
                <AccordionTrigger className="text-sm">Terms History</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    {[...termsHistory].reverse().map((entry, index) => (
                      <div key={index} className="border-l-2 border-brand-gold/40 pl-4">
                        <div className="text-xs text-brand-muted">
                          {new Date(entry.timestamp).toLocaleString()}
                        </div>
                        <div className="font-medium text-sm text-brand-text">
                          {entry.actor} {entry.action}
                        </div>
                        <div className="text-brand-gold font-semibold">
                          {entry.commissionPct}%
                        </div>
                        {entry.notes && (
                          <div className="text-xs text-brand-muted mt-1">
                            {entry.notes}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}
        </CardContent>
      </Card>

      {/* Counter Modal */}
      <Dialog open={counterModalOpen} onOpenChange={setCounterModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Counter Offer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Commission (%)</Label>
              <Input
                type="number"
                min="0"
                max="60"
                step="0.5"
                value={counterPct}
                onChange={(e) => setCounterPct(e.target.value)}
                placeholder="0-60"
                data-testid="counter-commission-input"
              />
              <p className="text-xs text-brand-muted mt-1">
                Enter a value between 0 and 60%
              </p>
            </div>

            <div>
              <Label>Notes (Optional)</Label>
              <Textarea
                value={counterNotes}
                onChange={(e) => setCounterNotes(e.target.value)}
                placeholder="Explain your counter offer..."
                rows={3}
                data-testid="counter-notes-input"
              />
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setCounterModalOpen(false)}
                className="flex-1"
                disabled={countering}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCounter}
                className="flex-1 bg-brand-gold hover:bg-brand-gold/90 text-black"
                disabled={countering}
                data-testid="submit-counter-button"
                aria-busy={countering}
              >
                {countering && <Spinner className="mr-2" />}
                {countering ? 'Submitting…' : 'Send Counter Offer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TermsCard;
